import {CommonModule} from '@angular/common';
import {Component, DestroyRef, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatSelectModule} from '@angular/material/select';
import {ActivatedRoute, Router} from '@angular/router';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import {
  ConnectConnectionPermissions,
  ConnectConnectionsService,
  ConnectServerConnection,
} from '../../../services/connect/connect-connections.service';
import {ClientStateService} from '../../../services/auth/client-state.service';
import {
  ConnectGuidedPriorStepBinding,
  ConnectNodesService,
  ConnectWorkflowNodePaletteItem,
  parseConnectGuidedPriorStepBindingToken,
  projectConnectGuidedConnectorOperation,
  resolveConnectNodeOperation,
} from '../../../services/connect/connect-nodes.service';
import {
  ConnectRun,
  ConnectWorkflow,
  ConnectWorkflowsService,
} from '../../../services/connect/connect-workflows.service';
import {
  compileGuidedWorkflow,
  compileGuidedWorkflowWithTrace,
  ConnectGuidedActionBlock,
  ConnectGuidedBlock,
  ConnectGuidedConnectorOperationBlock,
  ConnectGuidedConnectorProjector,
  ConnectGuidedDecisionCondition,
  ConnectGuidedPriorityDecisionBlock,
  ConnectGuidedWorkflowOutline,
} from './connect-guided-workflow-compiler';
import {ConnectGuidedWorkflowJsonAdapterSession} from './connect-guided-workflow-json-adapter';
import {
  ConnectGuidedModuleDraft,
  ConnectGuidedModuleEditorComponent,
} from './connect-guided-module-editor.component';

type GuidedRunPath = 'urgent' | 'standard' | null;
type ConnectGuidedBuiltInActionKind = Exclude<
  ConnectGuidedActionBlock['kind'],
  'connector-operation'
>;

type GuidedLoopRunSummary = {
  nodeId: string;
  processed: number;
  succeeded: number;
  failed: number;
};

type GuidedModuleLocation = {
  container: 'top' | 'urgent' | 'standard';
  index?: number;
  loopIndex?: number;
};

type GuidedAccountOption = {
  id: string;
  name: string;
  providerRef: string;
  service: string;
};

type GuidedModuleEditorState = {
  location: GuidedModuleLocation;
  node: ConnectWorkflowNodePaletteItem;
  draft: ConnectGuidedModuleDraft | null;
  connectionId: string;
};

@Component({
  selector: 'app-connect-guided-workflow-builder',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    ConnectGuidedModuleEditorComponent,
  ],
  templateUrl: './connect-guided-workflow-builder.component.html',
  styleUrl: './connect-guided-workflow-builder.component.scss',
})
export class ConnectGuidedWorkflowBuilderComponent {
  private readonly workflows = inject(ConnectWorkflowsService);
  private readonly connections = inject(ConnectConnectionsService);
  private readonly nodes = inject(ConnectNodesService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly clientState = inject(ClientStateService);
  private readonly clientReset$ = new Subject<void>();
  private clientGeneration = 0;

  readonly workflowForm = new FormGroup({
    name: new FormControl('Route an internal request', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(80)],
    }),
  });

  readonly testForm = new FormGroup({
    subject: new FormControl('Help with an account', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(120)],
    }),
    details: new FormControl('Synthetic local example', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(500)],
    }),
    priority: new FormControl('Urgent', {nonNullable: true, validators: [Validators.required]}),
    items: new FormControl('First example item\nSecond example item', {
      nonNullable: true,
      validators: [Validators.maxLength(1000)],
    }),
  });

  workflow: ConnectWorkflow | null = null;
  loading = false;
  saving = false;
  running = false;
  loadError = false;
  saveError = false;
  runError = false;
  decisionValueInputError = false;
  runPath: GuidedRunPath = null;
  lastRun: ConnectRun | null = null;
  localPreviewAvailable = false;
  moduleCatalogState: 'loading' | 'ready' | 'empty' | 'error' = 'loading';
  moduleCatalog: ConnectWorkflowNodePaletteItem[] = [];
  moduleEditor: GuidedModuleEditorState | null = null;
  moduleAccounts: GuidedAccountOption[] = [];
  moduleAccountState: 'idle' | 'loading' | 'ready' | 'empty' | 'error' = 'idle';
  confirmProviderChanges = false;
  private localPreviewConnectionId: string | null = null;
  private connectionInventoryState: 'loading' | 'ready' | 'error' = 'loading';
  private serverConnections: ConnectServerConnection[] = [];
  private readonly moduleConnectionBindings = new Map<string, string>();
  outline: ConnectGuidedWorkflowOutline = this.createDefaultOutline();
  private savedDraft: ConnectGuidedWorkflowOutline | null = null;
  private testedDraft: ConnectGuidedWorkflowOutline | null = null;
  private workflowJsonSession: ConnectGuidedWorkflowJsonAdapterSession | null = null;

  get saved(): boolean {
    return typeof this.workflow?.id === 'string' && this.workflow.id.length > 0
      || Boolean(this.route.snapshot.paramMap.get('id'));
  }

  get draft(): ConnectGuidedWorkflowOutline {
    return {
      name: this.workflowForm.controls.name.value,
      blocks: this.cloneBlocks(this.outline.blocks),
    };
  }

  get topLevelSteps(): ConnectGuidedBlock[] {
    return this.decision ? this.outline.blocks.slice(0, -1) : this.outline.blocks;
  }

  get decision(): ConnectGuidedPriorityDecisionBlock | null {
    return this.findDecision(this.outline.blocks);
  }

  get decisionCondition(): ConnectGuidedDecisionCondition | null {
    const decision = this.decision;
    return decision ? decision.condition ??= this.createDefaultCondition() : null;
  }

  get decisionSummary(): string {
    const condition = this.decisionCondition;
    if (!condition) return '';
    const field = condition.field === 'priority'
      ? 'Priority'
      : condition.field === 'subject' ? 'Subject' : 'Details';
    const operator = condition.operator === 'equals' ? 'is' : 'is not';
    return `${field} ${operator} ${condition.value || 'a value'}`;
  }

  get usesLocalPreview(): boolean {
    return this.blocksUseLocalPreview(this.outline.blocks);
  }

  get hasLoop(): boolean {
    return this.blocksContainLoop(this.outline.blocks);
  }

  get hasUnsavedChanges(): boolean {
    if (!this.saved) return false;
    return this.workflowJsonSession
      ? this.workflowJsonSession.hasChanges(this.workflowForm.controls.name.value)
      : JSON.stringify(this.draft) !== JSON.stringify(this.savedDraft);
  }

  get runOutcome(): string {
    if (!this.testedDraft || !this.runPath) return '';
    const decision = this.findDecision(this.testedDraft.blocks);
    return this.runPath === 'urgent'
      ? decision?.urgent.outcome ?? ''
      : decision?.standard.outcome ?? '';
  }

  get runSucceeded(): boolean {
    return this.lastRun?.status === 'succeeded';
  }

  get completedStepCount(): number {
    return (this.lastRun?.steps ?? []).filter(step => step.status === 'succeeded').length;
  }

  get loopInputItems(): string[] {
    return this.testForm.controls.items.value.split(/\r?\n/)
      .map(item => item.trim()).filter(Boolean).slice(0, 10);
  }

  get loopRunSummaries(): GuidedLoopRunSummary[] {
    return (this.lastRun?.steps ?? [])
      .filter(step => step.type === 'control.loop' && this.isRecord(step.output))
      .map(step => {
        const output = step.output as Record<string, unknown>;
        const iterations = Array.isArray(output['iterations_data']) ? output['iterations_data'] : [];
        const statuses = iterations
          .filter(item => this.isRecord(item))
          .map(item => (item as Record<string, unknown>)['status']);
        return {
          nodeId: step.node_id,
          processed: typeof output['iterations'] === 'number' ? output['iterations'] : iterations.length,
          succeeded: statuses.filter(status => status === 'succeeded').length,
          failed: statuses.filter(status => status !== 'succeeded').length,
        };
      });
  }

  constructor() {
    this.clientState.resetCache$.pipe(
      filter(reset => reset),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.reloadForClient());
    this.reloadForClient();
  }

  save(): void {
    if (this.moduleEditor || this.loadError || this.route.snapshot.paramMap.get('id') && !this.workflowJsonSession) {
      this.saveError = true;
      return;
    }

    if (this.decisionValueInputError) {
      this.saveError = true;
      return;
    }

    if (this.workflowForm.invalid) {
      this.workflowForm.markAllAsTouched();
      return;
    }

    const draft = this.draft;
    let compiled: ConnectWorkflow;
    try {
      if (this.saved) {
        if (!this.workflowJsonSession) throw new Error('Workflow JSON session is unavailable.');
        compiled = this.workflowJsonSession.serialize(this.workflowForm.controls.name.value);
      } else {
        compiled = compileGuidedWorkflow(draft, this.moduleCatalog);
      }
    } catch {
      this.saveError = true;
      return;
    }
    const generation = this.clientGeneration;
    this.saving = true;
    this.saveError = false;
    const request = this.saved
      ? this.workflows.updateWorkflow(this.workflow!.id as string, {...compiled, id: this.workflow!.id})
      : this.workflows.createWorkflow(compiled);

    request.pipe(finalize(() => {
      if (generation === this.clientGeneration) this.saving = false;
    })).subscribe({
      next: workflow => {
        if (generation !== this.clientGeneration) return;
        const session = ConnectGuidedWorkflowJsonAdapterSession.open(
          workflow,
          this.projectConnector,
          this.moduleCatalog,
        );
        if (!session) {
          this.workflow = workflow;
          this.workflowJsonSession = null;
          this.savedDraft = null;
          this.testedDraft = null;
          this.loadError = true;
          this.saveError = true;
          return;
        }
        this.installWorkflowSession(workflow, session);
        if (workflow.id) {
          void this.router.navigate(['/connect/workflows', workflow.id, 'guided'], {replaceUrl: true});
        }
      },
      error: () => {
        if (generation === this.clientGeneration) this.saveError = true;
      },
    });
  }

  run(): void {
    const session = this.workflowJsonSession;
    if (this.moduleEditor || this.loadError || !this.workflow?.id || !session
      || !session.canRunFromGuidedEditor || this.testForm.invalid || this.hasUnsavedChanges) {
      this.testForm.markAllAsTouched();
      this.runError = true;
      return;
    }

    const draft = this.savedDraft ?? this.draft;
    const mutatesProvider = this.blocksHaveMutatingModules(draft.blocks);
    if (mutatesProvider && !this.confirmProviderChanges) {
      this.runError = true;
      return;
    }

    const generation = this.clientGeneration;
    const workflowId = this.workflow.id as string;
    const triggerNodeId = session.getTriggerNodeId();
    this.running = true;
    this.runError = false;
    this.runPath = null;
    this.lastRun = null;
    this.testedDraft = this.cloneOutline(draft);
    if (mutatesProvider) this.confirmProviderChanges = false;
    const rawInput = this.testForm.getRawValue();
    const input = {
      subject: rawInput.subject,
      details: rawInput.details,
      priority: rawInput.priority,
      items: rawInput.items.split(/\r?\n/).map(item => item.trim()).filter(Boolean).slice(0, 10).map(subject => ({
        subject,
        details: rawInput.details,
        priority: rawInput.priority,
      })),
    };
    this.resolveConnectionBindings(draft.blocks).pipe(
      switchMap(bindings => {
        if (generation !== this.clientGeneration) return EMPTY;
        if (!bindings) return throwError(() => new Error('No verified connected account is available.'));
        return Object.keys(bindings).length > 0
          ? this.workflows.runConfiguredTrigger(workflowId, triggerNodeId, {
            input,
            connectionBindings: bindings,
          })
          : this.workflows.runTrigger(workflowId, triggerNodeId, input);
      }),
      switchMap(({runId}) => generation === this.clientGeneration
        ? timer(0, 1000).pipe(
          switchMap(() => this.workflows.getRun(runId).pipe(timeout({first: 20_000}))),
          take(60),
          takeWhile(run => !this.isTerminal(run.status), true),
        )
        : EMPTY),
      takeUntil(this.clientReset$),
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        if (generation === this.clientGeneration) {
          this.running = false;
          if (mutatesProvider) this.confirmProviderChanges = false;
        }
      }),
    ).subscribe({
      next: run => {
        if (generation !== this.clientGeneration) return;
        if (!this.isTerminal(run.status)) return;
        this.lastRun = run;
        if (run.status !== 'succeeded') {
          this.runError = true;
          return;
        }
        const decision = this.findDecision(this.testedDraft?.blocks ?? []);
        this.runPath = !decision ? null : run.steps?.some(step =>
          session.isOutcomeNodeId(step.node_id, 'urgent') && step.status === 'succeeded')
          ? 'urgent'
          : 'standard';
      },
      error: () => {
        if (generation === this.clientGeneration) this.runError = true;
      },
      complete: () => {
        if (generation === this.clientGeneration && !this.lastRun) this.runError = true;
      },
    });
  }

  backToWorkflows(): void {
    void this.router.navigate(['/connect/workflows']);
  }

  backToAdvancedWorkflows(): void {
    void this.router.navigate(['/connect/workflows/advanced']);
  }

  addTopLevelStep(kind: ConnectGuidedBuiltInActionKind | 'for-each-item'): void {
    const steps = [...this.topLevelSteps];
    steps.push(this.createBlock(kind));
    this.replaceTopLevelSteps(steps);
    this.clearRunResult();
  }

  addDecision(): void {
    if (this.decision) return;
    this.outline.blocks = [...this.outline.blocks, this.createDecision()];
    this.clearRunResult();
  }

  removeDecision(): void {
    const decision = this.decision;
    if (!decision || decision.urgent.blocks.length || decision.standard.blocks.length) return;
    this.outline.blocks = [...this.topLevelSteps];
    this.decisionValueInputError = false;
    this.clearRunResult();
  }

  openModulePicker(location: GuidedModuleLocation, node?: ConnectWorkflowNodePaletteItem): void {
    if (this.moduleCatalogState !== 'ready') return;
    const current = this.blockAt(location);
    const selectedNode = node
      ?? (current?.kind === 'connector-operation' ? this.moduleDescriptor(current)?.node : undefined)
      ?? this.moduleCatalog[0];
    if (!selectedNode) return;
    this.moduleEditor = {
      location,
      node: selectedNode,
      draft: this.existingModuleDraft(location, selectedNode),
      connectionId: this.existingModuleConnectionId(location),
    };
    this.refreshModuleAccounts(selectedNode);
  }

  selectModuleNode(paletteId: string): void {
    const state = this.moduleEditor;
    const node = this.moduleCatalog.find(candidate => candidate.paletteId === paletteId);
    if (!state || !node) return;
    this.moduleEditor = {...state, node, draft: null, connectionId: ''};
    this.refreshModuleAccounts(node);
  }

  setModuleDraft(draft: ConnectGuidedModuleDraft | null): void {
    if (!this.moduleEditor || draft && draft.paletteId !== this.moduleEditor.node.paletteId) return;
    this.moduleEditor = {...this.moduleEditor, draft};
  }

  priorStepBindingsFor(location: GuidedModuleLocation): readonly ConnectGuidedPriorStepBinding[] {
    if (location.loopIndex !== undefined || this.moduleCatalogState !== 'ready') return [];
    let earlier: ConnectGuidedBlock[];
    if (location.container === 'top') {
      earlier = this.topLevelSteps.slice(0, location.index ?? this.topLevelSteps.length);
    } else {
      const branch = this.decision?.[location.container].blocks ?? [];
      earlier = [
        ...this.topLevelSteps,
        ...branch.slice(0, location.index ?? branch.length),
      ];
    }
    let nodeIds: Map<ConnectGuidedBlock, string>;
    try {
      nodeIds = this.workflowJsonSession
        ? this.workflowJsonSession.previewBlockNodeIds()
        : compileGuidedWorkflowWithTrace(this.outline, undefined, this.moduleCatalog).blockNodeIds;
    } catch {
      return [];
    }
    const result: ConnectGuidedPriorStepBinding[] = [];
    const seen = new Set<string>();
    for (const block of earlier) {
      const operationRef = block.kind === 'connector-operation'
        ? block.operationRef
        : block.kind === 'verify-local-account'
          ? 'local.preview.verify'
          : null;
      const nodeId = nodeIds.get(block);
      const descriptor = operationRef ? resolveConnectNodeOperation(this.moduleCatalog, operationRef) : null;
      if (!nodeId || !descriptor || !operationRef) continue;
      for (const [outputPath, shape] of Object.entries(descriptor.output)) {
        const value = `$.${nodeId}.output.${outputPath}`;
        if (!parseConnectGuidedPriorStepBindingToken(value) || seen.has(value)) return [];
        seen.add(value);
        result.push({
          value: value as ConnectGuidedPriorStepBinding['value'],
          label: `${this.outputLabel(outputPath)} from ${descriptor.operation.label}`,
          producerNodeId: nodeId,
          operationRef,
          outputPath,
          shape,
        });
      }
    }
    return result;
  }

  selectModuleConnection(connectionId: string): void {
    if (!this.moduleEditor || !this.moduleAccounts.some(option => option.id === connectionId)) return;
    this.moduleEditor = {...this.moduleEditor, connectionId};
  }

  closeModulePicker(): void {
    this.moduleEditor = null;
    this.moduleAccounts = [];
    this.moduleAccountState = 'idle';
  }

  retryModuleAccountInventory(): void {
    const editor = this.moduleEditor;
    if (!editor || this.moduleAccountState !== 'error') return;
    if (this.connectionInventoryState === 'error') {
      this.connectionInventoryState = 'loading';
      this.moduleAccountState = 'loading';
      this.loadAccountOptions(this.clientGeneration);
      return;
    }
    if (this.connectionInventoryState === 'ready') this.refreshModuleAccounts(editor.node);
  }

  applyModule(): void {
    const editor = this.moduleEditor;
    const account = this.moduleAccounts.find(option => option.id === editor?.connectionId);
    if (!editor?.draft || !account || editor.draft.providerId !== account.service) {
      this.saveError = true;
      return;
    }
    const current = this.blockAt(editor.location);
    const retainsAuthority = current?.kind === 'connector-operation'
      && current.operationRef === editor.draft.ref
      && current.connectionSlot === account.name
      && current.connectionProviderRef === account.providerRef;
    const block: ConnectGuidedConnectorOperationBlock = retainsAuthority
      ? current
      : {
        kind: 'connector-operation',
        operationRef: editor.draft.ref,
        connectionSlot: account.name,
        connectionProviderRef: account.providerRef,
        input: {},
      };
    block.input = this.cloneValue(editor.draft.input);
    this.writeModuleAt(editor.location, block);
    this.moduleConnectionBindings.set(account.name, account.id);
    this.closeModulePicker();
    this.confirmProviderChanges = false;
    this.clearRunResult();
  }

  canAddStep(blocks: ConnectGuidedBlock[], _kind: ConnectGuidedBuiltInActionKind | 'for-each-item'): boolean {
    return blocks.length < 10;
  }

  addBranchStep(
    branch: 'urgent' | 'standard',
    kind: ConnectGuidedBuiltInActionKind | 'for-each-item',
  ): void {
    this.decision?.[branch].blocks.push(this.createBlock(kind));
    this.clearRunResult();
  }

  removeTopLevelStep(index: number): void {
    const steps = [...this.topLevelSteps];
    steps.splice(index, 1);
    this.replaceTopLevelSteps(steps);
    this.clearRunResult();
  }

  removeBranchStep(branch: 'urgent' | 'standard', index: number): void {
    this.decision?.[branch].blocks.splice(index, 1);
    this.clearRunResult();
  }

  moveTopLevelStep(index: number, direction: -1 | 1): void {
    const steps = [...this.topLevelSteps];
    this.moveInList(steps, index, direction);
    this.replaceTopLevelSteps(steps);
    this.clearRunResult();
  }

  moveBranchStep(branch: 'urgent' | 'standard', index: number, direction: -1 | 1): void {
    const blocks = this.decision?.[branch].blocks;
    if (blocks) this.moveInList(blocks, index, direction);
    this.clearRunResult();
  }

  setLoopBody(
    blocks: ConnectGuidedBlock[],
    index: number,
    kind: ConnectGuidedBuiltInActionKind,
  ): void {
    const block = blocks[index];
    if (block?.kind !== 'for-each-item') return;
    block.body = [this.createBlock(kind)];
    this.clearRunResult();
  }

  setDecisionField(field: ConnectGuidedDecisionCondition['field']): void {
    if (this.decisionCondition) this.decisionCondition.field = field;
    this.clearRunResult();
  }

  setDecisionOperator(operator: ConnectGuidedDecisionCondition['operator']): void {
    if (this.decisionCondition) this.decisionCondition.operator = operator;
    this.clearRunResult();
  }

  setDecisionValue(value: string): void {
    if (/[\r\n]/.test(value)) {
      this.rejectDecisionValueInput();
      return;
    }
    this.decisionValueInputError = false;
    this.saveError = false;
    if (this.decisionCondition) this.decisionCondition.value = value;
    this.clearRunResult();
  }

  onDecisionValuePaste(event: ClipboardEvent): void {
    if (!/[\r\n]/.test(event.clipboardData?.getData('text/plain') ?? '')) return;
    event.preventDefault();
    this.rejectDecisionValueInput();
  }

  onDecisionValueBeforeInput(event: InputEvent): void {
    const containsLineBreak = event.inputType === 'insertLineBreak'
      || event.inputType === 'insertParagraph'
      || /[\r\n]/.test(event.data ?? '');
    if (!containsLineBreak) return;
    event.preventDefault();
    this.rejectDecisionValueInput();
  }

  stepTitle(block: ConnectGuidedBlock): string {
    if (block.kind === 'prepare-information') return 'Prepare information';
    if (block.kind === 'verify-local-account') return 'Check a connected account';
    if (block.kind === 'for-each-item') return 'For every item';
    if (block.kind === 'connector-operation') {
      return this.moduleDescriptor(block)?.operation.label ?? 'Connected app step';
    }
    return 'Make a decision';
  }

  stepDescription(block: ConnectGuidedBlock): string {
    if (block.kind === 'prepare-information') return 'Keep subject, details and priority together.';
    if (block.kind === 'verify-local-account') return 'Verify the Intuvio local preview account before continuing.';
    if (block.kind === 'for-each-item') return `Run “${this.stepTitle(block.body[0])}” once for each item (up to 10).`;
    if (block.kind === 'connector-operation') {
      const descriptor = this.moduleDescriptor(block);
      return descriptor
        ? `${descriptor.node.label} · ${this.effectLabel(descriptor.operation.effect)}`
        : 'This connected app step is unavailable in the current catalog.';
    }
    return 'Choose the next steps from the request priority.';
  }

  private reloadForClient(): void {
    this.clientReset$.next();
    const generation = ++this.clientGeneration;
    this.clearClientState();
    this.loadAccountOptions(generation);
    const workflowId = this.route.snapshot.paramMap.get('id');
    this.loadModuleCatalog(generation, workflowId);
  }

  private clearClientState(): void {
    this.workflow = null;
    this.workflowJsonSession = null;
    this.savedDraft = null;
    this.testedDraft = null;
    this.loading = false;
    this.saving = false;
    this.running = false;
    this.loadError = false;
    this.saveError = false;
    this.runError = false;
    this.decisionValueInputError = false;
    this.runPath = null;
    this.lastRun = null;
    this.localPreviewConnectionId = null;
    this.localPreviewAvailable = false;
    this.moduleCatalogState = 'loading';
    this.moduleCatalog = [];
    this.moduleEditor = null;
    this.moduleAccounts = [];
    this.moduleAccountState = 'idle';
    this.connectionInventoryState = 'loading';
    this.serverConnections = [];
    this.moduleConnectionBindings.clear();
    this.confirmProviderChanges = false;
    this.outline = this.createDefaultOutline();
    this.workflowForm.reset({name: this.outline.name});
  }

  private load(workflowId: string, generation: number): void {
    this.loading = true;
    this.loadError = false;
    this.workflows.getWorkflow(workflowId).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        if (generation === this.clientGeneration) this.loading = false;
      }),
    ).subscribe({
      next: workflow => {
        if (generation !== this.clientGeneration) return;
        const session = ConnectGuidedWorkflowJsonAdapterSession.open(
          workflow,
          this.projectConnector,
          this.moduleCatalog,
        );
        if (!session) {
          this.loadError = true;
          return;
        }
        this.installWorkflowSession(workflow, session);
      },
      error: () => {
        if (generation === this.clientGeneration) this.loadError = true;
      },
    });
  }

  private loadAccountOptions(generation: number): void {
    this.connections.list().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: connections => {
        if (generation !== this.clientGeneration) return;
        this.connectionInventoryState = 'ready';
        this.serverConnections = connections.map(connection => ({...connection}));
        const localPreview = connections.find(connection =>
          connection.ref === 'local.preview' || connection.provider_ref === 'local.preview');
        this.localPreviewConnectionId = localPreview?.id?.trim() || null;
        this.localPreviewAvailable = this.localPreviewConnectionId !== null;
        this.rebuildModuleBindings();
        if (this.moduleEditor) this.refreshModuleAccounts(this.moduleEditor.node);
      },
      error: () => {
        if (generation !== this.clientGeneration) return;
        this.connectionInventoryState = 'error';
        this.localPreviewConnectionId = null;
        this.localPreviewAvailable = false;
        this.serverConnections = [];
        this.moduleConnectionBindings.clear();
        if (this.moduleEditor) this.refreshModuleAccounts(this.moduleEditor.node);
      },
    });
  }

  private loadModuleCatalog(generation: number, workflowId: string | null): void {
    this.nodes.fetchCatalog().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: catalog => {
        if (generation !== this.clientGeneration) return;
        const modules = catalog.nodes.filter(node =>
          node.workflowType === 'api.connector'
          && node.presentation?.operations.length
          && node.providerId,
        );
        const refs = new Set<string>();
        const duplicate = modules.some(node => node.presentation!.operations.some(operation => {
          const ref = `${node.paletteId}.${operation.action}`;
          if (refs.has(ref)) return true;
          refs.add(ref);
          return false;
        }));
        this.moduleCatalog = duplicate ? [] : modules;
        this.moduleCatalogState = duplicate ? 'error' : modules.length ? 'ready' : 'empty';
        if (workflowId) this.load(workflowId, generation);
      },
      error: () => {
        if (generation !== this.clientGeneration) return;
        this.moduleCatalog = [];
        this.moduleCatalogState = 'error';
        if (workflowId) this.load(workflowId, generation);
      },
    });
  }

  private readonly projectConnector: ConnectGuidedConnectorProjector = (node, providerRef) => {
    return projectConnectGuidedConnectorOperation(node, providerRef, this.moduleCatalog);
  };

  private outputLabel(path: string): string {
    const label = path.split('.').at(-1)?.replace(/[-_]+/g, ' ') ?? 'Value';
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  private refreshModuleAccounts(node: ConnectWorkflowNodePaletteItem): void {
    const generation = this.clientGeneration;
    const providerId = node.providerId?.trim();
    this.moduleAccounts = [];
    this.moduleAccountState = 'loading';
    if (!providerId) {
      this.moduleAccountState = 'empty';
      return;
    }
    if (this.connectionInventoryState === 'loading') return;
    if (this.connectionInventoryState === 'error') {
      this.moduleAccountState = 'error';
      return;
    }
    if (this.serverConnections.length === 0) {
      this.moduleAccountState = 'empty';
      return;
    }
    forkJoin(this.serverConnections.map(connection => this.connections.getPermissions(
      connection.id, {service: providerId},
    ).pipe(
      map(permissions => ({connection, permissions, failed: false})),
      catchError(() => of({connection, permissions: null, failed: true})),
    ))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(results => {
      if (generation !== this.clientGeneration || this.moduleEditor?.node.paletteId !== node.paletteId) return;
      if (results.some(result => result.failed)) {
        this.moduleAccountState = 'error';
        return;
      }
      const candidates = results.flatMap(({connection, permissions}) =>
        this.toGuidedAccountOption(connection, permissions, providerId));
      this.moduleAccounts = candidates.filter(candidate => candidates.filter(other =>
        other.name === candidate.name && other.providerRef === candidate.providerRef).length === 1);
      this.moduleAccountState = this.moduleAccounts.length ? 'ready' : 'empty';
      const current = this.moduleEditor?.connectionId;
      if (this.moduleEditor && !this.moduleAccounts.some(option => option.id === current)
          && this.moduleAccounts.length === 1) {
        this.moduleEditor = {...this.moduleEditor, connectionId: this.moduleAccounts[0].id};
      }
    });
  }

  private toGuidedAccountOption(
    connection: ConnectServerConnection,
    permissions: ConnectConnectionPermissions | null,
    providerId: string,
  ): GuidedAccountOption[] {
    const providerRef = (connection.provider_ref ?? connection.ref)?.trim();
    const service = permissions?.service?.trim();
    const usable = Boolean(connection.id?.trim() && connection.name?.trim() && providerRef
      && service === providerId && permissions?.connected === true
      && permissions.hasRequiredScopes === true && connection.connected !== false
      && connection.oauth_connected !== false);
    return usable ? [{
      id: connection.id.trim(),
      name: connection.name.trim(),
      providerRef: providerRef as string,
      service: service as string,
    }] : [];
  }

  private existingModuleDraft(
    location: GuidedModuleLocation,
    node: ConnectWorkflowNodePaletteItem,
  ): ConnectGuidedModuleDraft | null {
    const block = this.blockAt(location);
    if (block?.kind !== 'connector-operation') return null;
    const descriptor = this.moduleDescriptor(block);
    if (!descriptor || descriptor.node.paletteId !== node.paletteId) return null;
    return {
      paletteId: descriptor.node.paletteId,
      workflowType: descriptor.node.workflowType,
      ...(descriptor.node.providerId ? {providerId: descriptor.node.providerId} : {}),
      action: descriptor.operation.action,
      ref: block.operationRef,
      effect: descriptor.operation.effect,
      input: this.cloneValue(block.input),
    };
  }

  private existingModuleConnectionId(location: GuidedModuleLocation): string {
    const block = this.blockAt(location);
    if (block?.kind !== 'connector-operation') return '';
    return this.moduleConnectionBindings.get(block.connectionSlot) ?? '';
  }

  private writeModuleAt(location: GuidedModuleLocation, block: ConnectGuidedConnectorOperationBlock): void {
    if (location.loopIndex !== undefined) {
      const container = this.containerAt(location.container);
      const loop = container[location.loopIndex];
      if (loop?.kind !== 'for-each-item') return;
      loop.body = [block];
      this.commitTopContainer(location.container, container);
      return;
    }
    const container = this.containerAt(location.container);
    if (location.index === undefined) container.push(block);
    else container[location.index] = block;
    this.commitTopContainer(location.container, container);
  }

  private blockAt(location: GuidedModuleLocation): ConnectGuidedBlock | null {
    const container = this.containerAt(location.container);
    if (location.loopIndex !== undefined) {
      const loop = container[location.loopIndex];
      return loop?.kind === 'for-each-item' ? loop.body[0] ?? null : null;
    }
    return location.index === undefined ? null : container[location.index] ?? null;
  }

  private containerAt(container: GuidedModuleLocation['container']): ConnectGuidedBlock[] {
    if (container === 'top') return [...this.topLevelSteps];
    return [...(this.decision?.[container].blocks ?? [])];
  }

  private commitTopContainer(container: GuidedModuleLocation['container'], blocks: ConnectGuidedBlock[]): void {
    if (container === 'top') this.replaceTopLevelSteps(blocks);
    else if (this.decision) this.decision[container].blocks = blocks;
  }

  private moduleDescriptor(block: ConnectGuidedConnectorOperationBlock) {
    const matches = this.moduleCatalog.flatMap(node => (node.presentation?.operations ?? [])
      .filter(operation => `${node.paletteId}.${operation.action}` === block.operationRef)
      .map(operation => ({node, operation})));
    return matches.length === 1 ? matches[0] : null;
  }

  private resolveConnectionBindings(blocks: ConnectGuidedBlock[]): Observable<Record<string, string> | null> {
    const bindings = Object.create(null) as Record<string, string>;
    if (this.blocksUseLocalPreview(blocks)) {
      if (!this.localPreviewConnectionId) return of(null);
      bindings['local_preview'] = this.localPreviewConnectionId;
    }
    const modules = this.collectModuleBlocks(blocks).filter(block => !this.isLocalPreviewModule(block));
    if (modules.length === 0) return of(bindings);
    const checks = modules.map(block => {
      const descriptor = this.moduleDescriptor(block);
      const providerId = descriptor?.node.providerId?.trim();
      const exactMatches = this.serverConnections.filter(connection =>
        connection.name?.trim() === block.connectionSlot
        && (connection.provider_ref ?? connection.ref)?.trim() === block.connectionProviderRef
        && connection.connected !== false && connection.oauth_connected !== false);
      if (!providerId || exactMatches.length !== 1) return of(null);
      const connection = exactMatches[0];
      return this.connections.getPermissions(connection.id, {service: providerId}).pipe(
        map(permissions => {
          const verified = this.toGuidedAccountOption(connection, permissions, providerId);
          return verified.length === 1 ? {slot: block.connectionSlot, id: verified[0].id} : null;
        }),
        catchError(() => of(null)),
      );
    });
    return forkJoin(checks).pipe(map(results => {
      if (results.some(result => result === null)) return null;
      for (const result of results) {
        if (!result) return null;
        if (Object.hasOwn(bindings, result.slot) && bindings[result.slot] !== result.id) return null;
        bindings[result.slot] = result.id;
      }
      return bindings;
    }));
  }

  private collectModuleBlocks(blocks: ConnectGuidedBlock[]): ConnectGuidedConnectorOperationBlock[] {
    return blocks.flatMap(block => block.kind === 'connector-operation'
      ? [block]
      : block.kind === 'for-each-item'
        ? this.collectModuleBlocks(block.body)
        : block.kind === 'priority-decision'
          ? [...this.collectModuleBlocks(block.urgent.blocks), ...this.collectModuleBlocks(block.standard.blocks)]
          : []);
  }

  private blocksHaveMutatingModules(blocks: ConnectGuidedBlock[]): boolean {
    return this.collectModuleBlocks(blocks).some(block => {
      const effect = this.moduleDescriptor(block)?.operation.effect;
      return effect === 'write' || effect === 'delete';
    });
  }

  moduleEffect(block: ConnectGuidedBlock): 'read' | 'write' | 'delete' | null {
    return block.kind === 'connector-operation' ? this.moduleDescriptor(block)?.operation.effect ?? null : null;
  }

  effectLabel(effect: 'read' | 'write' | 'delete'): string {
    if (effect === 'read') return 'Reads information';
    if (effect === 'write') return 'Changes information';
    return 'Removes information';
  }

  moduleAccountName(block: ConnectGuidedBlock): string {
    return block.kind === 'connector-operation' ? block.connectionSlot : '';
  }

  moduleNode(block: ConnectGuidedBlock): ConnectWorkflowNodePaletteItem | null {
    return block.kind === 'connector-operation' ? this.moduleDescriptor(block)?.node ?? null : null;
  }

  moduleLocation(
    container: GuidedModuleLocation['container'],
    index?: number,
    loopIndex?: number,
  ): GuidedModuleLocation {
    return {
      container,
      ...(index === undefined ? {} : {index}),
      ...(loopIndex === undefined ? {} : {loopIndex}),
    };
  }

  get hasMutatingModules(): boolean {
    return this.blocksHaveMutatingModules(this.outline.blocks);
  }

  get hasModules(): boolean {
    return this.collectModuleBlocks(this.outline.blocks).length > 0;
  }

  private cloneValue<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
  }

  private isTerminal(status: string): boolean {
    return ['succeeded', 'failed', 'cancelled', 'timed_out'].includes(status);
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private createDefaultOutline(): ConnectGuidedWorkflowOutline {
    return {
      name: 'Route an internal request',
      blocks: [
        {kind: 'prepare-information'},
      ],
    };
  }

  private createDecision(): ConnectGuidedPriorityDecisionBlock {
    return {
      kind: 'priority-decision',
      condition: this.createDefaultCondition(),
      urgent: {blocks: [], outcome: 'Ready for immediate review'},
      standard: {blocks: [], outcome: 'Ready for normal review'},
    };
  }

  private replaceTopLevelSteps(steps: ConnectGuidedBlock[]): void {
    const decision = this.decision;
    this.outline.blocks = decision ? [...steps, decision] : steps;
  }

  private createDefaultCondition(): ConnectGuidedDecisionCondition {
    return {field: 'priority', operator: 'equals', value: 'Urgent'};
  }

  private createBlock(
    kind: ConnectGuidedBuiltInActionKind | 'for-each-item',
  ): ConnectGuidedActionBlock | {kind: 'for-each-item'; body: ConnectGuidedBlock[]} {
    return kind === 'for-each-item'
      ? {kind, body: [{kind: 'prepare-information'}]}
      : {kind};
  }

  private moveInList(list: ConnectGuidedBlock[], index: number, direction: -1 | 1): void {
    const target = index + direction;
    if (index < 0 || target < 0 || index >= list.length || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
  }

  private blocksUseLocalPreview(blocks: ConnectGuidedBlock[]): boolean {
    return blocks.some(block => block.kind === 'verify-local-account'
      || block.kind === 'connector-operation' && this.isLocalPreviewModule(block)
      || block.kind === 'for-each-item' && this.blocksUseLocalPreview(block.body)
      || block.kind === 'priority-decision' && (
        this.blocksUseLocalPreview(block.urgent.blocks)
        || this.blocksUseLocalPreview(block.standard.blocks)
      ));
  }

  private isLocalPreviewModule(block: ConnectGuidedConnectorOperationBlock): boolean {
    return block.operationRef === 'local.preview.verify'
      && block.connectionSlot === 'local_preview'
      && block.connectionProviderRef === 'local.preview';
  }

  private blocksContainLoop(blocks: ConnectGuidedBlock[]): boolean {
    return blocks.some(block => block.kind === 'for-each-item'
      || block.kind === 'priority-decision' && (
        this.blocksContainLoop(block.urgent.blocks)
        || this.blocksContainLoop(block.standard.blocks)
      ));
  }

  private findDecision(blocks: ConnectGuidedBlock[]): ConnectGuidedPriorityDecisionBlock | null {
    const decision = blocks.find(block => block.kind === 'priority-decision');
    return decision?.kind === 'priority-decision' ? decision : null;
  }

  private clearRunResult(): void {
    this.runPath = null;
    this.lastRun = null;
    this.runError = false;
  }

  private rejectDecisionValueInput(): void {
    this.decisionValueInputError = true;
    this.saveError = true;
    this.clearRunResult();
  }

  private cloneOutline(outline: ConnectGuidedWorkflowOutline): ConnectGuidedWorkflowOutline {
    return {name: outline.name, blocks: this.cloneBlocks(outline.blocks)};
  }

  private cloneBlocks(blocks: ConnectGuidedBlock[]): ConnectGuidedBlock[] {
    return JSON.parse(JSON.stringify(blocks)) as ConnectGuidedBlock[];
  }

  private installWorkflowSession(
    workflow: ConnectWorkflow,
    session: ConnectGuidedWorkflowJsonAdapterSession,
  ): void {
    this.workflow = workflow;
    this.workflowJsonSession = session;
    this.outline = session.outline;
    this.workflowForm.setValue({name: session.outline.name});
    this.savedDraft = this.cloneOutline(this.draft);
    this.rebuildModuleBindings();
  }

  private rebuildModuleBindings(): void {
    this.moduleConnectionBindings.clear();
  }
}
